import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ShieldCheck, FileText, LogOut, Menu } from "lucide-react";

export default function SiteHeader({ variant = "public" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);
  return (
    <header className="border-b border-zinc-200 bg-white/95 backdrop-blur-sm sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="site-logo">
          <div className="w-8 h-8 grid place-items-center bg-[#0F172A] text-white rounded-sm">
            <ShieldCheck className="w-4 h-4" />
          </div>
          <div className="leading-none">
            <div className="font-heading text-lg font-bold tracking-tight">CoS Verify</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">UKVI GENUINITY</div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm">
          <Link to="/" className="text-zinc-600 hover:text-zinc-900" data-testid="nav-home">Home</Link>
          {user ? (
            <>
              <Link to="/app" className="text-zinc-600 hover:text-zinc-900" data-testid="nav-check">Check</Link>
              <Link to="/app/history" className="text-zinc-600 hover:text-zinc-900" data-testid="nav-history">History</Link>
              <Link to="/app/team" className="text-zinc-600 hover:text-zinc-900" data-testid="nav-team">Team</Link>
              {user.role === "admin" && (
                <>
                  <Link to="/app/admin/portal" className="text-zinc-600 hover:text-zinc-900" data-testid="nav-admin-portal">Portal</Link>
                  <Link to="/app/admin/reviews" className="text-zinc-600 hover:text-zinc-900" data-testid="nav-admin-inbox">Inbox</Link>
                </>
              )}
            </>
          ) : (
            <>
              <a href="/#pricing" className="text-zinc-600 hover:text-zinc-900" data-testid="nav-pricing">Pricing</a>
              <a href="#how-it-works" className="text-zinc-600 hover:text-zinc-900">How it works</a>
            </>
          )}
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <>
              <span className="hidden sm:inline font-mono text-xs text-zinc-500" data-testid="header-user-email">{user.email}</span>
              <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={async () => { await logout(); navigate("/"); }} data-testid="header-logout-btn">
                <LogOut className="w-3.5 h-3.5" /> Logout
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" className="hidden md:inline-flex" onClick={() => navigate("/login")} data-testid="header-login-btn">Sign in</Button>
              <Button size="sm" className="hidden md:inline-flex bg-[#0F172A] hover:bg-[#0F172A]/90" onClick={() => navigate("/register")} data-testid="header-register-btn">
                <FileText className="w-3.5 h-3.5" /> Get started
              </Button>
            </>
          )}
          {/* Mobile menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="md:hidden h-9 w-9" data-testid="mobile-menu-trigger" aria-label="Open menu">
                <Menu className="w-4 h-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 p-0" data-testid="mobile-menu-panel">
              <div className="p-6 border-b border-zinc-200">
                <div className="font-heading text-lg font-bold tracking-tight">CoS Verify</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">UKVI GENUINITY</div>
                {user && <div className="mt-3 font-mono text-xs text-zinc-500 truncate">{user.email}</div>}
              </div>
              <nav className="flex flex-col p-3 text-sm">
                <Link to="/" onClick={closeMobile} className="px-3 py-3 rounded-sm hover:bg-zinc-100" data-testid="mobile-nav-home">Home</Link>
                {user ? (
                  <>
                    <Link to="/app" onClick={closeMobile} className="px-3 py-3 rounded-sm hover:bg-zinc-100" data-testid="mobile-nav-check">Check</Link>
                    <Link to="/app/history" onClick={closeMobile} className="px-3 py-3 rounded-sm hover:bg-zinc-100" data-testid="mobile-nav-history">History</Link>
                    <Link to="/app/team" onClick={closeMobile} className="px-3 py-3 rounded-sm hover:bg-zinc-100" data-testid="mobile-nav-team">Team</Link>
                    {user.role === "admin" && (
                      <>
                        <Link to="/app/admin/portal" onClick={closeMobile} className="px-3 py-3 rounded-sm hover:bg-zinc-100" data-testid="mobile-nav-admin-portal">Admin Portal</Link>
                        <Link to="/app/admin/reviews" onClick={closeMobile} className="px-3 py-3 rounded-sm hover:bg-zinc-100" data-testid="mobile-nav-admin-inbox">Expert Inbox</Link>
                      </>
                    )}
                    <button onClick={async () => { closeMobile(); await logout(); navigate("/"); }}
                            className="mt-3 mx-2 inline-flex items-center gap-2 px-3 py-2 border border-zinc-300 rounded-sm text-left hover:bg-zinc-100"
                            data-testid="mobile-logout-btn">
                      <LogOut className="w-3.5 h-3.5" /> Logout
                    </button>
                  </>
                ) : (
                  <>
                    <a href="/#pricing" onClick={closeMobile} className="px-3 py-3 rounded-sm hover:bg-zinc-100" data-testid="mobile-nav-pricing">Pricing</a>
                    <button onClick={() => { closeMobile(); navigate("/login"); }}
                            className="mt-3 mx-2 px-3 py-2 border border-zinc-300 rounded-sm text-left hover:bg-zinc-100"
                            data-testid="mobile-login-btn">Sign in</button>
                    <button onClick={() => { closeMobile(); navigate("/register"); }}
                            className="mt-2 mx-2 px-3 py-2 rounded-sm bg-[#0F172A] text-white text-left hover:bg-[#0F172A]/90"
                            data-testid="mobile-register-btn">Get started</button>
                  </>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
